import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UsersIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  EyeIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalUsers: number;
  totalStudents: number;
  totalEmployers: number;
  totalJobs: number;
  totalCourses: number;
  totalApplications: number;
  totalCertifications: number;
  pendingVerifications: number;
  activeSubscriptions: number;
  revenueThisMonth: number;
  newUsersThisWeek: number;
  completedCoursesThisMonth: number;
}

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: number;
  dbConnections: number;
  memoryUsage: number;
  cpuUsage: number;
  apiResponseTime: number;
}

const AdminDashboard: React.FC = () => {
  const { adminUser, hasPermission, refreshAdminData } = useAdmin();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, healthData] = await Promise.all([
        adminApiService.getDashboardStats(),
        adminApiService.getSystemHealth()
      ]);
      
      setStats(statsData);
      setSystemHealth(healthData);
    } catch (error: any) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMigratePermissions = async () => {
    try {
      // First check admin status for debugging
      const statusResult = await adminApiService.getCurrentAdminStatus();
      if (statusResult.success) {
        const { currentAdmin, existsInCollection, totalAdmins, needsMigration } = statusResult.data;
        
        console.log('🔍 Admin Status Debug:', {
          currentAdmin,
          existsInCollection,
          totalAdmins,
          needsMigration
        });
        
        if (!needsMigration && existsInCollection) {
          toast.success('✅ Permissions are already up to date!');
          // Refresh admin data to ensure latest permissions are loaded
          await refreshAdminData();
          toast.success('🔄 Permissions refreshed successfully!');
          return;
        }
        
        if (totalAdmins === 0) {
          toast('🔧 No admin users found - creating your admin account...', {
            duration: 4000,
            icon: 'ℹ️'
          });
        }
      }
      
      if (!window.confirm('This will update/create admin permissions to include all features. Continue?')) {
        return;
      }
      
      // Proceed with migration
      const result = await adminApiService.migrateAdminPermissions();
      if (result.success) {
        toast.success(result.message || 'Permissions updated successfully');
        
        // If a new admin was created or permissions updated, refresh admin data
        if (result.data?.created || result.data?.updatedCount > 0) {
          setTimeout(async () => {
            toast('🔄 Applying new permissions...', {
              duration: 2000,
              icon: '🔄'
            });
            await refreshAdminData();
            toast.success('✅ New permissions are now active!');
          }, 1000);
        }
      } else {
        toast.error(result.message || 'Failed to migrate permissions');
      }
    } catch (error: any) {
      console.error('Permission migration error:', error);
      toast.error('Failed to migrate permissions');
    }
  };

  const handleCreateSampleData = async () => {
    if (!window.confirm('This will create sample users, jobs, and courses for testing. Continue?')) {
      return;
    }

    try {
      const result = await adminApiService.createSampleData();
      if (result.success) {
        toast.success(`Sample data created successfully: ${result.data.students} students, ${result.data.employers} employers, ${result.data.jobs} jobs, ${result.data.courses} courses`);
        // Refresh dashboard data
        fetchDashboardData();
      } else {
        toast.error(result.message || 'Failed to create sample data');
      }
    } catch (error: any) {
      console.error('Sample data creation error:', error);
      toast.error('Failed to create sample data');
    }
  };

  const handleCreateSampleNotifications = async () => {
    if (!window.confirm('This will create sample notifications for testing. Continue?')) {
      return;
    }

    try {
      const result = await adminApiService.createSampleNotifications();
      if (result.success) {
        toast.success('Sample notifications created successfully');
      } else {
        toast.error(result.message || 'Failed to create sample notifications');
      }
    } catch (error: any) {
      console.error('Create sample notifications error:', error);
      toast.error('Failed to create sample notifications');
    }
  };

  const handleRefreshPermissions = async () => {
    try {
      toast('🔄 Refreshing permissions...', { icon: '🔄' });
      await refreshAdminData();
      toast.success('✅ Permissions refreshed successfully!');
    } catch (error: any) {
      console.error('Refresh permissions error:', error);
      toast.error('Failed to refresh permissions');
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ComponentType<any>;
    color: string;
    change?: number;
  }> = ({ title, value, icon: Icon, color, change }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${
              change >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <ArrowTrendingUpIcon className={`h-4 w-4 mr-1 ${change < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(change)}% from last month
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );

  const QuickAction: React.FC<{
    title: string;
    description: string;
    icon: React.ComponentType<any>;
    onClick: () => void;
    color: string;
  }> = ({ title, description, icon: Icon, onClick, color }) => (
    <button
      onClick={onClick}
      className="w-full p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 text-left"
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${color} mr-3`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Welcome back, {adminUser?.name}
              </p>
            </div>
            
            {/* System Health Indicator */}
            {systemHealth && (
              <div className="flex items-center space-x-2">
                <div className={`h-3 w-3 rounded-full ${
                  systemHealth.status === 'healthy' ? 'bg-green-500' :
                  systemHealth.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <span className="text-sm text-gray-600">
                  System {systemHealth.status}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Actions - Temporary Migration Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Admin Permission Update Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Update admin permissions to access Email Management and Support Tickets features.
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleMigratePermissions}
                className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <CogIcon className="h-4 w-4 mr-1" />
                Update Permissions
              </button>
              <button
                onClick={handleCreateSampleData}
                className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-800 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Create Sample Data
              </button>
              <button
                onClick={handleCreateSampleNotifications}
                className="inline-flex items-center px-3 py-2 border border-purple-300 shadow-sm text-sm leading-4 font-medium rounded-md text-purple-800 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Sample Notifications
              </button>
              <button
                onClick={handleRefreshPermissions}
                className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-green-800 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                🔄 Refresh Permissions
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats && (
            <>
              <StatCard
                title="Total Users"
                value={stats.totalUsers.toLocaleString()}
                icon={UsersIcon}
                color="bg-blue-500"
                change={12}
              />
              <StatCard
                title="Active Jobs"
                value={stats.totalJobs.toLocaleString()}
                icon={BriefcaseIcon}
                color="bg-green-500"
                change={8}
              />
              <StatCard
                title="Courses"
                value={stats.totalCourses.toLocaleString()}
                icon={AcademicCapIcon}
                color="bg-purple-500"
                change={15}
              />
              <StatCard
                title="Revenue (Month)"
                value={`$${stats.revenueThisMonth.toLocaleString()}`}
                icon={CurrencyDollarIcon}
                color="bg-yellow-500"
                change={22}
              />
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                {hasPermission('users', 'read') && (
                  <QuickAction
                    title="Manage Users"
                    description="View and manage user accounts"
                    icon={UsersIcon}
                    color="bg-blue-500"
                    onClick={() => window.location.href = '/admin/users'}
                  />
                )}
                
                {hasPermission('jobs', 'read') && (
                  <QuickAction
                    title="Review Jobs"
                    description="Moderate job postings"
                    icon={BriefcaseIcon}
                    color="bg-green-500"
                    onClick={() => window.location.href = '/admin/jobs'}
                  />
                )}
                
                {hasPermission('verifications', 'read') && (
                  <QuickAction
                    title="Verification Queue"
                    description={`${stats?.pendingVerifications || 0} pending verifications`}
                    icon={CheckCircleIcon}
                    color="bg-orange-500"
                    onClick={() => window.location.href = '/admin/verifications'}
                  />
                )}
                
                {hasPermission('reports', 'read') && (
                  <QuickAction
                    title="Generate Reports"
                    description="Create detailed analytics reports"
                    icon={DocumentTextIcon}
                    color="bg-purple-500"
                    onClick={() => window.location.href = '/admin/reports'}
                  />
                )}
                
                {hasPermission('system', 'read') && (
                  <QuickAction
                    title="System Settings"
                    description="Configure platform settings"
                    icon={CogIcon}
                    color="bg-gray-500"
                    onClick={() => window.location.href = '/admin/settings'}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity & Alerts */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform Overview</h2>
              
              {/* Secondary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats?.totalStudents || 0}
                  </div>
                  <div className="text-sm text-gray-600">Students</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats?.totalEmployers || 0}
                  </div>
                  <div className="text-sm text-gray-600">Employers</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats?.totalApplications || 0}
                  </div>
                  <div className="text-sm text-gray-600">Applications</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">
                    {stats?.totalCertifications || 0}
                  </div>
                  <div className="text-sm text-gray-600">Certifications</div>
                </div>
              </div>

              {/* System Health Details */}
              {systemHealth && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">System Health</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Uptime:</span>
                      <span className="ml-2 font-medium">
                        {Math.floor(systemHealth.uptime / 3600)}h {Math.floor((systemHealth.uptime % 3600) / 60)}m
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Memory:</span>
                      <span className="ml-2 font-medium">{systemHealth.memoryUsage}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600">CPU:</span>
                      <span className="ml-2 font-medium">{systemHealth.cpuUsage}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Response Time:</span>
                      <span className="ml-2 font-medium">{systemHealth.apiResponseTime}ms</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">This Week</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">New users registered</span>
                    <span className="font-medium text-blue-600">
                      +{stats?.newUsersThisWeek || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Courses completed</span>
                    <span className="font-medium text-green-600">
                      {stats?.completedCoursesThisMonth || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Active subscriptions</span>
                    <span className="font-medium text-purple-600">
                      {stats?.activeSubscriptions || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 